import apisauce, { ApiResponse, ApisauceInstance } from 'apisauce';

import NavActions from '~utils/navActions';
import UtilFunctions from '~utils/functions';
import {
    GatewayResponseAwaited,
    GatewayResponseStatus,
    GatewayResponseError,
    GatewayResponseSuccess,
    THeaders,
    TApiError,
} from '~types/gateway';

const { Auth: { clearAuthToken } } = UtilFunctions;

export default class Gateway {
    protected api: ApisauceInstance;
    protected apiNoTimeout: ApisauceInstance;

    constructor(baseURL: string) {
        const headers: THeaders = {
            'Content-Type': 'application/json',
        };

        const config = {
            baseURL,
            headers,
            timeout: 60000,
            withCredentials: true,
        };

        this.api = apisauce.create(config);
        this.apiNoTimeout = apisauce.create({ ...config, timeout: 0 });
    }

    process<T>(response: ApiResponse<T | TApiError>): GatewayResponseAwaited<T> {
        const { ok, problem, data, status } = response;

        if (status === 401) {
            const { error = '' } = data as TApiError;

            NavActions.navResetToLogin();
            clearAuthToken();

            return {
                status: GatewayResponseStatus.Error,
                code: '401',
                name: 'Unauthorized',
                message: error || 'Unauthorized',
            } as GatewayResponseError;
        }

        if (!ok || problem) {
            let name = 'Error';
            let message = 'Something went wrong. Please try again.';

            if (problem === 'NETWORK_ERROR') {
                name = 'No Internet connnection';
                message = 'There appears to be no Internet connection. Please check your Internet connection.';
            }

            if (problem === 'CLIENT_ERROR') {
                if (typeof data === 'string') message = data;
                else if (data) {
                    const { code = '', error = '' } = data as TApiError;

                    if (code && error) {
                        return {
                            status: GatewayResponseStatus.Error,
                            code: problem,
                            name: code.toString(),
                            message: error,
                        };
                    }
                }
            }

            if (problem === 'TIMEOUT_ERROR') {
                name = 'Connection timed out';
                message = 'Connection timed out. Please try again.';
            }

            return {
                status: GatewayResponseStatus.Error,
                code: problem,
                name,
                message,
            } as GatewayResponseError;
        }

        return {
            status: GatewayResponseStatus.Success,
            data,
        } as GatewayResponseSuccess<T>;
    }
}
